/**
 * MiniAnalyticsBar — ambient at-a-glance numbers for the dashboard.
 *
 * Lives quietly under the hero metric row. Uses the `.metric-strip`
 * pattern: a single connected card with subtle dividers between
 * tiles, so the row reads as one ambient surface rather than four
 * competing chips. Numbers are rendered in the Outfit display face
 * to match every other "real number" surface in the app.
 */
import { useState } from 'react';
import { Inbox, FolderOpen, FileText, DollarSign, type LucideIcon } from 'lucide-react';
import { useInquiries } from '@/services/inquiries';
import { useProjects } from '@/services/projects';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { demoStats } from '@/data/demoOverlay';
import { MetricDetailSheet } from './MetricDetailSheet';

interface Tile {
  key: string;
  icon: LucideIcon;
  label: string;
  value: string | number;
  href: string;
  cta: string;
  context: string;
}

export function MiniAnalyticsBar() {
  const { isDemo } = useDemoMode();
  const { data: inquiries = [] } = useInquiries();
  const { data: projects = [] } = useProjects();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const today = new Date().toDateString();
  const inquiriesToday = inquiries.filter((i) => {
    const date = (i as any).submitted_at || (i as any).submittedAt;
    return date && new Date(date).toDateString() === today;
  }).length;

  const activeProjects = projects.filter(
    (p) => p.status !== 'delivered',
  ).length;

  // Contracts sent + payments received don't have a single live query yet,
  // so when isDemo is on we substitute the precomputed studio-shaped numbers
  // from demoOverlay so the strip looks alive in screenshots.
  const contractsSent = isDemo ? demoStats.contractsSent : 0;
  const paymentsReceived = isDemo ? demoStats.paymentsReceivedFormatted : '$0';

  const tiles: Tile[] = [
    { key: 'inquiries-today', icon: Inbox, label: 'Inquiries today', value: inquiriesToday, href: '/inquiries', cta: 'Open inquiries', context: `New inquiries received today: ${inquiriesToday}.` },
    { key: 'projects-active', icon: FolderOpen, label: 'Projects active', value: activeProjects, href: '/projects', cta: 'Open projects', context: `Projects not yet delivered: ${activeProjects}.` },
    { key: 'contracts-sent', icon: FileText, label: 'Contracts sent', value: contractsSent, href: '/projects', cta: 'Open projects', context: `Contracts sent this period: ${contractsSent}.` },
    { key: 'payments-received', icon: DollarSign, label: 'Payments received', value: paymentsReceived, href: '/settings?tab=payments', cta: 'Open payments', context: `Payments received this period: ${paymentsReceived}.` },
  ];

  return (
    <>
      <div className="metric-strip animate-fade-in">
        {tiles.map((tile) => (
          <button
            type="button"
            key={tile.key}
            onClick={() => setOpenKey(tile.key)}
            className="metric-tile text-left focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-xl transition-colors hover:bg-muted/40"
          >
            <div className="metric-tile-icon">
              <tile.icon className="w-4 h-4" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="metric-tile-label">{tile.label}</span>
              <span className="metric-tile-value">{tile.value}</span>
            </div>
          </button>
        ))}
      </div>
      {tiles.map((tile) => (
        <MetricDetailSheet
          key={tile.key}
          open={openKey === tile.key}
          onOpenChange={(o) => setOpenKey(o ? tile.key : null)}
          icon={tile.icon}
          eyebrow="At a glance"
          label={tile.label}
          value={tile.value}
          context={tile.context}
          cta={{ label: tile.cta, href: tile.href }}
        />
      ))}
    </>
  );
}
